import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {setGateMode} from '@/entities/device/api';
import type {DeviceDetail, GateMode} from '@/entities/device/types';
import {Segmented} from '@/shared/ui/segmented';
import {AppText} from '@/shared/ui/text';
import {toast} from '@/shared/ui/toast';
import {spacing} from '@/shared/ui/theme';

/**
 * The gate direction a terminal enforces, as a three-way segment.
 *
 * The write is optimistic: pressing a segment flips the cached DeviceDetail at once
 * (the terminal picks it up on its next sync, not instantly), so the UI never waits on
 * the round trip. A failure rolls the cache back; either way it settles by
 * invalidating so the server's truth wins in the end.
 *
 * Смена режима — одно из немногих действий кабинета, у которого есть НАСТОЯЩИЙ обратный
 * вызов: вернуть прежний режим это тот же законный запрос. Поэтому здесь у тоста есть
 * «Отменить» — и поэтому его нет там, где отзыв сессии или удаление отдела откатить
 * нечем (см. модульный комментарий в `shared/ui/toast`).
 */
const MODES: readonly GateMode[] = ['entry', 'exit', 'both'];

const styles = StyleSheet.create({
  field: {gap: spacing(2)},
});

export function GateModeSwitch({
  companyId,
  deviceId,
  current,
}: {
  companyId: string;
  deviceId: string;
  /** The device's current gate_mode (free-form string on the summary). */
  current: string;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const key = ['device', companyId, deviceId];

  const mutation = useMutation({
    mutationFn: (mode: GateMode) => setGateMode(companyId, deviceId, mode),
    onMutate: async (mode: GateMode) => {
      await qc.cancelQueries({queryKey: key});
      const prev = qc.getQueryData<DeviceDetail>(key);
      if (prev) {
        qc.setQueryData<DeviceDetail>(key, {
          ...prev,
          summary: {...prev.summary, gate_mode: mode},
        });
      }
      return {prev};
    },
    onError: (_err, _mode, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(key, ctx.prev);
      }
    },
    // Тост поднимается здесь, а не объявляется через `meta`, потому что ему нужен
    // ПРЕЖНИЙ режим: без него кнопке отмены нечего восстанавливать. Он же не
    // показывается на самом откате — иначе вышла бы качель «отменить отмену».
    onSuccess: (_data, mode, ctx) => {
      const previous = ctx?.prev?.summary.gate_mode as GateMode | undefined;
      toast.success(t('toast.gateMode'), {
        action:
          previous && previous !== mode
            ? {label: t('actions.undo'), onPress: () => mutation.mutate(previous)}
            : undefined,
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({queryKey: key});
    },
  });

  return (
    <View style={styles.field}>
      <AppText variant="caption">{t('home.device.gateMode')}</AppText>
      <Segmented
        testID="gate-mode"
        options={MODES.map(mode => ({
          value: mode,
          label: t(`home.device.gate.${mode}`),
        }))}
        // `current` is a free-form string on the summary; anything the server sends
        // that is not one of the three simply leaves no segment selected, which is
        // honest — better than silently showing "entry" for an unknown mode.
        value={current as GateMode}
        disabled={mutation.isPending}
        onChange={mode => mutation.mutate(mode)}
      />
    </View>
  );
}
